<template>
  <div class="subheader flex flex-col bg-white border-b border-grey-light px-4-x py-2-x">
    <div class="actions-group flex justify-between items-center">

      <!-- Lado izquierdo -->
      <div class="flex items-center space-x-2">

        <template v-for="item in leftItems">

          <!-- H4 + H5: button / toggle CON tooltip -->
          <b-tooltip
            v-if="isPlainItem(item) && resolvedTooltip(item)"
            :key="`${item.id}:tt`"
            :label="resolvedTooltip(item)"
            position="is-bottom">
            <div
              :id="`subheader-${item.id}`"
              class="flex items-center space-x-2 px-3 py-1.5 cursor-pointer transition-all duration-150 ease-out"
              :class="itemClasses(item)"
              :aria-label="resolvedLabel(item)"
              @click="onItemClick(item)">
              <b-icon
                v-if="resolvedIcon(item)"
                size="is-small"
                :icon="resolvedIcon(item)"
                :pack="resolvedIconPack(item)" />
              <span v-if="resolvedLabel(item)" class="text-xs font-semibold">
                {{ resolvedLabel(item) }}
              </span>
            </div>
          </b-tooltip>

          <!-- H4 + H5: button / toggle SIN tooltip -->
          <div
            v-else-if="isPlainItem(item)"
            :key="item.id"
            :id="`subheader-${item.id}`"
            class="flex items-center space-x-2 px-3 py-1.5 cursor-pointer transition-all duration-150 ease-out"
            :class="itemClasses(item)"
            :aria-label="resolvedLabel(item)"
            @click="onItemClick(item)">
            <b-icon
              v-if="resolvedIcon(item)"
              size="is-small"
              :icon="resolvedIcon(item)"
              :pack="resolvedIconPack(item)" />
            <span v-if="resolvedLabel(item)" class="text-xs font-semibold">
              {{ resolvedLabel(item) }}
            </span>
          </div>

          <!-- H6: dropdown -->
          <b-dropdown
            v-else-if="item.type === 'dropdown'"
            :key="item.id"
            aria-role="list"
            :position="dropdownPosition(item)"
            :disabled="isDisabled(item)">
            <template #trigger>
              <p role="button"
                 :id="`subheader-${item.id}`"
                 class="flex items-center space-x-1 cursor-pointer px-3 py-1.5 transition-all duration-150 ease-out"
                 :class="itemClasses(item)">
                <b-icon
                  v-if="resolvedIcon(item)"
                  size="is-small"
                  :icon="resolvedIcon(item)"
                  :pack="resolvedIconPack(item)" />
                <span v-if="resolvedLabel(item)" class="text-xs font-semibold">
                  {{ resolvedLabel(item) }}
                </span>
                <b-icon icon="chevron-down" pack="fas" size="is-small" />
              </p>
            </template>
            <b-dropdown-item
              v-for="opt in resolvedOptions(item)"
              :key="opt.value"
              aria-role="listitem"
              @click="onDropdownOptionSelected(item, opt)">
              <b-icon v-if="opt.icon" :icon="opt.icon" :pack="opt.iconPack || 'fas'" size="is-small" />
              {{ opt.labelKey ? $t(opt.labelKey) : (opt.label || '') }}
            </b-dropdown-item>
          </b-dropdown>

        </template>

        <slot name="left-extra" :context="context" />
      </div>

      <!-- H7: slot center opcional -->
      <slot name="center" :context="context" />

      <!-- Lado derecho -->
      <div class="flex items-center space-x-2">
        <slot name="right-extra" :context="context" />

        <template v-for="item in rightItems">

          <!-- H4 + H5: button / toggle CON tooltip -->
          <b-tooltip
            v-if="isPlainItem(item) && resolvedTooltip(item)"
            :key="`${item.id}:tt`"
            :label="resolvedTooltip(item)"
            position="is-bottom">
            <div
              :id="`subheader-${item.id}`"
              class="flex items-center space-x-2 px-3 py-1.5 cursor-pointer transition-all duration-150 ease-out"
              :class="itemClasses(item)"
              :aria-label="resolvedLabel(item)"
              @click="onItemClick(item)">
              <b-icon
                v-if="resolvedIcon(item)"
                size="is-small"
                :icon="resolvedIcon(item)"
                :pack="resolvedIconPack(item)" />
              <span v-if="resolvedLabel(item)" class="text-xs font-semibold">
                {{ resolvedLabel(item) }}
              </span>
            </div>
          </b-tooltip>

          <!-- H4 + H5: button / toggle SIN tooltip -->
          <div
            v-else-if="isPlainItem(item)"
            :key="item.id"
            :id="`subheader-${item.id}`"
            class="flex items-center space-x-2 px-3 py-1.5 cursor-pointer transition-all duration-150 ease-out"
            :class="itemClasses(item)"
            :aria-label="resolvedLabel(item)"
            @click="onItemClick(item)">
            <b-icon
              v-if="resolvedIcon(item)"
              size="is-small"
              :icon="resolvedIcon(item)"
              :pack="resolvedIconPack(item)" />
            <span v-if="resolvedLabel(item)" class="text-xs font-semibold">
              {{ resolvedLabel(item) }}
            </span>
          </div>

          <!-- H6: dropdown -->
          <b-dropdown
            v-else-if="item.type === 'dropdown'"
            :key="item.id"
            aria-role="list"
            :position="dropdownPosition(item)"
            :disabled="isDisabled(item)">
            <template #trigger>
              <p role="button"
                 :id="`subheader-${item.id}`"
                 class="flex items-center space-x-1 cursor-pointer px-3 py-1.5 transition-all duration-150 ease-out"
                 :class="itemClasses(item)">
                <b-icon
                  v-if="resolvedIcon(item)"
                  size="is-small"
                  :icon="resolvedIcon(item)"
                  :pack="resolvedIconPack(item)" />
                <span v-if="resolvedLabel(item)" class="text-xs font-semibold">
                  {{ resolvedLabel(item) }}
                </span>
                <b-icon icon="chevron-down" pack="fas" size="is-small" />
              </p>
            </template>
            <b-dropdown-item
              v-for="opt in resolvedOptions(item)"
              :key="opt.value"
              aria-role="listitem"
              @click="onDropdownOptionSelected(item, opt)">
              <b-icon v-if="opt.icon" :icon="opt.icon" :pack="opt.iconPack || 'fas'" size="is-small" />
              {{ opt.labelKey ? $t(opt.labelKey) : (opt.label || '') }}
            </b-dropdown-item>
          </b-dropdown>

        </template>
      </div>

    </div>
  </div>
</template>

<script>
const VALID_TYPES = ['button', 'toggle', 'dropdown']
const VALID_SIDES = ['left', 'right']

export default {
  name: 'Subheader',

  props: {
    /**
     * Definición declarativa de items.
     * Ver CONTRATO_SUBHEADER.md → Schema de item (`ItemSchema`).
     */
    items: {
      type: Array,
      default: () => [],
      validator(arr) {
        return arr.every(it =>
          it && typeof it.id === 'string' &&
          VALID_TYPES.includes(it.type) &&
          VALID_SIDES.includes(it.side)
        )
      },
    },

    /**
     * Contexto que se pasa como argumento a todas las funciones del schema
     * (visible, active, disabled, tooltipKey, payload, icon, etc.).
     * El padre decide qué meter; el Subheader nunca lee $store ni $refs.
     */
    context: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    /** Items con side='left', filtrados por visible. */
    leftItems() {
      return this.items.filter(it => it.side === 'left' && this.isVisible(it))
    },

    /** Items con side='right', filtrados por visible. */
    rightItems() {
      return this.items.filter(it => it.side === 'right' && this.isVisible(it))
    },
  },

  methods: {
    /**
     * Resuelve una propiedad del schema que puede ser:
     *  - un valor estático (string, boolean, number…)
     *  - una función (ctx) => valor
     */
    _resolve(value, ...args) {
      return typeof value === 'function' ? value(...args) : value
    },

    /** ¿El item debe renderizarse? Default true. */
    isVisible(item) {
      const v = this._resolve(item.visible, this.context)
      return v === undefined ? true : !!v
    },

    /** ¿Es un item simple/toggle? Helper para v-if/v-else-if del template. */
    isPlainItem(item) {
      return item.type === 'button' || item.type === 'toggle'
    },

    /**
     * Clases Tailwind aplicadas al item según su estado:
     *  - disabled: opacidad y sin pointer events
     *  - toggle activo: bg-blue + texto blanco
     *  - default (botón o toggle inactivo): texto gris oscuro con hover suave
     */
    itemClasses(item) {
      if (this.isDisabled(item)) {
        return 'cursor-not-allowed opacity-50 pointer-events-none'
      }
      if (item.type === 'toggle' && this.isActive(item)) {
        return 'bg-blue text-white hover:bg-blue-dark'
      }
      return 'text-grey-darkest hover:bg-grey-lighter hover:text-black'
    },

    /** ¿El item está activo? Solo significativo en 'toggle'. */
    isActive(item) {
      return !!this._resolve(item.active, this.context)
    },

    /** ¿El item está deshabilitado? Default false. */
    isDisabled(item) {
      return !!this._resolve(item.disabled, this.context)
    },

    /** Icono resuelto (puede ser función ctx => string). */
    resolvedIcon(item) {
      return this._resolve(item.icon, this.context)
    },

    /** Pack de iconos. Default 'fas'. */
    resolvedIconPack(item) {
      return this._resolve(item.iconPack, this.context) || 'fas'
    },

    /** Texto traducido del label (o null si no hay labelKey). */
    resolvedLabel(item) {
      const key = this._resolve(item.labelKey, this.context)
      return key ? this.$t(key) : null
    },

    /** Texto traducido del tooltip (o null si no hay tooltipKey). */
    resolvedTooltip(item) {
      const key = this._resolve(item.tooltipKey, this.context)
      return key ? this.$t(key) : null
    },

    /**
     * Lista de options del dropdown. Acepta tanto un array estático como
     * una función (ctx) => array, para permitir options dinámicas.
     */
    resolvedOptions(item) {
      return this._resolve(item.options, this.context) || []
    },

    /**
     * Posición del menú del b-dropdown según el lado del item:
     *  - left  → 'is-bottom-left'  (menú alineado por la izquierda)
     *  - right → 'is-bottom-right' (menú alineado por la derecha, evita overflow al borde)
     */
    dropdownPosition(item) {
      return item.side === 'right' ? 'is-bottom-right' : 'is-bottom-left'
    },

    /**
     * H9 — Emit único `@action`.
     * Payload:
     *  - Si schema define `payload`, usa ese (resuelve función).
     *  - Si es 'toggle', envía el nuevo estado booleano.
     *  - Si no, null.
     */
    onItemClick(item) {
      if (this.isDisabled(item)) return

      let payload
      if (item.payload !== undefined) {
        payload = this._resolve(item.payload, this.context)
      } else if (item.type === 'toggle') {
        payload = !this.isActive(item)
      } else {
        payload = null
      }

      this.$emit('action', {
        id: item.id,
        handler: item.handler,
        payload,
      })
    },

    /**
     * Handler del click sobre un b-dropdown-item.
     * Reemite por @action con la option como payload.
     */
    onDropdownOptionSelected(item, option) {
      if (this.isDisabled(item)) return

      this.$emit('action', {
        id: item.id,
        handler: item.handler,
        payload: option,
      })
    },
  },
}
</script>

<style scoped>
/* H8: estilos completos en paso siguiente. Por ahora solo lo mínimo
   para que el componente monte. La estética se hereda de Tailwind. */
</style>
